import { readSummaryMediaUrl } from '../dom-data.js';
import { scheduleIdle } from '../preload.js';
import { normalizeRoute } from '../routes.js';

function applySummaryVoStagger(root) {
  const animatedScreens = root.querySelectorAll('.summary-page__screen--animate');
  const baseDelay = 0.55;
  const step = 0.4;

  animatedScreens.forEach(function (screen) {
    const copy = screen.querySelector('.summary-page__copy');
    if (!copy) return;

    const lines = copy.querySelectorAll('.summary-page__title-line, .summary-page__bold-line');
    lines.forEach(function (el, index) {
      el.style.setProperty('--summary-vo-delay', baseDelay + index * step + 's');
    });
  });
}

export function createSummaryController(root, navigateTo, preloader) {
  if (!root) {
    return null;
  }

  const summaryRoot = root.querySelector('.summary-page');
  if (!summaryRoot) {
    return null;
  }

  const video = summaryRoot.querySelector('.summary-page__media');
  const screens = Array.from(summaryRoot.querySelectorAll('.summary-page__screen'));
  const mediaSrc = readSummaryMediaUrl(summaryRoot);
  const nextRoute = normalizeRoute(summaryRoot.getAttribute('data-next-href')) || 'finish';
  const refModal = summaryRoot.querySelector('#summaryRefModal');
  const refModalContent = summaryRoot.querySelector('#summaryRefModalContent');
  const refBtn = summaryRoot.querySelector('.summary-page__ref');
  const refCloseBtn = refModal ? refModal.querySelector('.slides-ref-modal__close') : null;
  const modalTpl = summaryRoot.querySelector('#summaryModalSource');

  const summaryPlayer = summaryRoot.querySelector('.summary-player');
  const spVideo = summaryPlayer ? summaryPlayer.querySelector('.summary-player__video') : null;
  const spAudio = summaryPlayer ? summaryPlayer.querySelector('.summary-player__audio') : null;
  const spGradeItems = summaryPlayer ? Array.from(summaryPlayer.querySelectorAll('.grade-item')) : [];
  const spHearingLabel = summaryPlayer ? summaryPlayer.querySelector('#summaryHearingLabel') : null;
  const spHearingIndicator = summaryPlayer ? summaryPlayer.querySelector('#summaryHearingIndicator') : null;
  const spProgressFill = summaryPlayer ? summaryPlayer.querySelector('#summaryProgressFill') : null;
  const spScreenIndex = summaryPlayer ? parseInt(summaryPlayer.getAttribute('data-screen-index'), 10) : -1;

  if (!video || !screens.length) {
    return null;
  }

  if (modalTpl && refModalContent) {
    refModalContent.innerHTML = modalTpl.innerHTML;
  }

  applySummaryVoStagger(summaryRoot);

  let active = false;
  let previousSummaryScreen = -1;
  let firstPointerHandler = null;
  let spHasStarted = false;
  let summaryPlaybackFinished = false;
  let summaryRefDelayPassed = false;
  let summaryRefDelayTimer = null;
  let summaryRefHidePassed = false;
  let summaryRefHideTimer = null;
  let spCurrentGrade = 'normal';
  const warmedSummaryGradeAudio = new Set();

  const spHearingLabels = {
    normal: 'NORMAL HEARING',
    mild: 'MILD HEARING LOSS',
    moderate: 'MODERATE HEARING LOSS',
    severe: 'SEVERE HEARING LOSS'
  };

  function spGetAudioSrc(grade) {
    if (!summaryPlayer) return '';
    if (grade === 'mild') return summaryPlayer.getAttribute('data-audio-mild') || '';
    if (grade === 'moderate') return summaryPlayer.getAttribute('data-audio-moderate') || '';
    if (grade === 'severe') return summaryPlayer.getAttribute('data-audio-severe') || '';
    return '';
  }

  function spSyncAudio(force) {
    if (!spAudio || !spVideo || spCurrentGrade === 'normal' || !spAudio.src) {
      return;
    }

    const targetTime = spVideo.currentTime || 0;
    const diff = Math.abs((spAudio.currentTime || 0) - targetTime);

    if (force || diff > 0.3) {
      try { spAudio.currentTime = targetTime; } catch (e) {}
    }
  }

  function spPlayAltTrack(grade) {
    if (!spAudio || !spVideo) return;

    const nextSrc = spGetAudioSrc(grade);
    if (!nextSrc) return;

    const prevSrc = spAudio.getAttribute('data-grade-src') || '';
    const targetTime = spVideo.currentTime || 0;

    function seekAndPlay() {
      try { spAudio.currentTime = targetTime; } catch (e) {}
      spAudio.play().catch(function () {});
    }

    if (prevSrc !== nextSrc) {
      spAudio.src = nextSrc;
      spAudio.setAttribute('data-grade-src', nextSrc);

      const onMeta = function () {
        spAudio.removeEventListener('loadedmetadata', onMeta);
        seekAndPlay();
      };

      spAudio.addEventListener('loadedmetadata', onMeta);
      spAudio.load();
      return;
    }

    seekAndPlay();
  }

  function warmSummaryGradeAudio() {
    if (!summaryPlayer) {
      return;
    }

    const audioSources = [
      summaryPlayer.getAttribute('data-audio-mild') || '',
      summaryPlayer.getAttribute('data-audio-moderate') || '',
      summaryPlayer.getAttribute('data-audio-severe') || ''
    ];

    audioSources.forEach(function (src) {
      if (!src || warmedSummaryGradeAudio.has(src)) {
        return;
      }

      const warmAudio = document.createElement('audio');
      warmAudio.preload = 'auto';
      warmAudio.src = src;
      try {
        warmAudio.load();
      } catch (e) {}
      warmedSummaryGradeAudio.add(src);
    });
  }

  function spApplyGrade(grade) {
    spCurrentGrade = grade;

    spGradeItems.forEach(function (item) {
      item.classList.toggle('active', item.dataset.grade === grade);
    });

    if (spHearingLabel) spHearingLabel.textContent = spHearingLabels[grade] || spHearingLabels.normal;
    if (spHearingIndicator) spHearingIndicator.classList.toggle('stopped', grade === 'normal');

    if (grade === 'normal') {
      if (spVideo) spVideo.muted = false;
      if (spAudio) {
        spAudio.pause();
        try { spAudio.currentTime = 0; } catch (e) {}
        spAudio.removeAttribute('src');
        spAudio.removeAttribute('data-grade-src');
      }
      return;
    }

    if (spVideo) spVideo.muted = true;
    spPlayAltTrack(grade);
  }

  function showSummaryPlayer() {
    if (!summaryPlayer || !spVideo) return;

    const videoSrc = summaryPlayer.getAttribute('data-video');
    if (!videoSrc) return;
    if (summaryPlaybackFinished) return;

    summaryPlayer.hidden = false;
    if (video) video.pause();

    if (spVideo.getAttribute('src') !== videoSrc) {
      spVideo.src = videoSrc;
      spHasStarted = false;
      summaryPlaybackFinished = false;
    }

    if (!spHasStarted) {
      try { spVideo.currentTime = 0; } catch (e) {}
      spVideo.muted = true;
      spVideo.play().then(function () {
        spVideo.muted = false;
      }).catch(function () {});
      spApplyGrade('normal');
      warmSummaryGradeAudio();
      spHasStarted = true;
      return;
    }

    if (spVideo.paused) {
      spVideo.play().catch(function () {});
    }
  }

  function hideSummaryPlayer() {
    if (!summaryPlayer) return;

    summaryPlayer.hidden = true;
    if (spVideo) spVideo.pause();
    if (spAudio) {
      spAudio.pause();
      try { spAudio.currentTime = 0; } catch (e) {}
    }
  }

  function syncSummaryRefVisibility(activeIndex) {
    if (!refBtn) return;

    refBtn.hidden = activeIndex !== 0
      || summaryPlaybackFinished
      || !summaryRefDelayPassed
      || summaryRefHidePassed
      || (refModal && !refModal.hidden);
  }

  function openSummaryRef() {
    if (refModal) refModal.hidden = false;
    syncSummaryRefVisibility(-1);
  }

  function closeSummaryRef() {
    if (refModal) refModal.hidden = true;
    syncSummaryRefVisibility(previousSummaryScreen);
  }

  function startSummaryRefDelay() {
    summaryRefDelayPassed = false;
    summaryRefHidePassed = false;

    if (summaryRefDelayTimer) window.clearTimeout(summaryRefDelayTimer);
    if (summaryRefHideTimer) window.clearTimeout(summaryRefHideTimer);

    summaryRefDelayTimer = window.setTimeout(function () {
      summaryRefDelayPassed = true;
      syncSummaryRefVisibility(previousSummaryScreen);

      summaryRefHideTimer = window.setTimeout(function () {
        summaryRefHidePassed = true;
        closeSummaryRef();
        syncSummaryRefVisibility(previousSummaryScreen);
      }, 8000);
    }, 1500);
  }

  function clearSummaryRefDelay() {
    summaryRefDelayPassed = false;
    summaryRefHidePassed = false;

    if (summaryRefDelayTimer) {
      window.clearTimeout(summaryRefDelayTimer);
      summaryRefDelayTimer = null;
    }

    if (summaryRefHideTimer) {
      window.clearTimeout(summaryRefHideTimer);
      summaryRefHideTimer = null;
    }
  }

  function setScreen(index) {
    const i = Math.max(0, Math.min(screens.length - 1, index));

    screens.forEach(function (el, screenIndex) {
      const isActive = screenIndex === i;
      el.classList.toggle('summary-page__screen--active', isActive);
      el.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    if (i !== previousSummaryScreen) {
      if (refModal && refBtn && i !== 0) {
        closeSummaryRef();
      }
      previousSummaryScreen = i;
    }

    if (i === 0 && !summaryPlaybackFinished) {
      startSummaryRefDelay();
    } else {
      clearSummaryRefDelay();
    }

    if (summaryPlayer && !summaryPlaybackFinished) {
      const shouldShowPlayer = i === spScreenIndex;
      if (shouldShowPlayer) {
        showSummaryPlayer();
      } else {
        hideSummaryPlayer();
      }
    }

    syncSummaryRefVisibility(i);
  }

  function summaryLastScreenLeadSec(duration) {
    if (!duration || duration <= 0) return 0;

    const base = Math.min(2, Math.max(0.2, duration * 0.04));
    return Math.min(base + 4, duration - 0.05);
  }

  function screenIndexForProgress(time, duration, ended) {
    if (!duration || duration <= 0 || screens.length === 0) return 0;

    const last = screens.length - 1;
    if (last === 0) return 0;
    if (ended) return last;

    const lead = summaryLastScreenLeadSec(duration);
    if (lead > 0 && duration - time <= lead) return last;

    const count = screens.length;
    if (count === 3) {
      return time / duration < 0.41 ? 0 : 1;
    }

    const slice = duration / (count - 1);
    return Math.min(last - 1, Math.max(0, Math.floor(time / slice)));
  }

  function armSummaryPlayback() {
    if (firstPointerHandler) {
      summaryRoot.removeEventListener('pointerdown', firstPointerHandler, true);
    }

    firstPointerHandler = function () {
      summaryRoot.removeEventListener('pointerdown', firstPointerHandler, true);
      firstPointerHandler = null;
      video.muted = false;
      video.play().catch(function () {});
    };

    summaryRoot.addEventListener('pointerdown', firstPointerHandler, true);
  }

  if (refBtn) {
    refBtn.addEventListener('click', function () {
      if (!active) return;
      openSummaryRef();
    });
  }

  if (refCloseBtn) {
    refCloseBtn.addEventListener('click', function () {
      if (!active) return;
      closeSummaryRef();
    });
  }

  document.addEventListener('keydown', function (event) {
    if (!active) return;
    if (event.key === 'Escape' && refModal && !refModal.hidden) {
      closeSummaryRef();
    }
  });

  video.addEventListener('timeupdate', function () {
    if (!active) return;

    const duration = video.duration;
    if (!duration || isNaN(duration)) return;

    setScreen(screenIndexForProgress(video.currentTime, duration, video.ended));
  });

  video.addEventListener('loadedmetadata', function () {
    if (!active) return;
    setScreen(0);
  });

  video.addEventListener('ended', function () {
    if (!active) return;

    if (summaryPlayer) {
      return;
    }

    setScreen(screens.length - 1);
    navigateTo(nextRoute);
  });

  if (summaryPlayer && spVideo) {
    spGradeItems.forEach(function (item) {
      item.addEventListener('click', function () {
        if (!active) return;
        spApplyGrade(item.dataset.grade || 'normal');
      });
    });

    spVideo.addEventListener('timeupdate', function () {
      if (!spVideo.duration) return;

      const progress = Math.min(100, (spVideo.currentTime / spVideo.duration) * 100);
      if (spProgressFill) spProgressFill.style.width = progress + '%';
      spSyncAudio(false);
    });

    spVideo.addEventListener('ended', function () {
      if (!active) return;
      if (spAudio) spAudio.pause();
      summaryPlaybackFinished = true;
      clearSummaryRefDelay();
      syncSummaryRefVisibility(-1);
      hideSummaryPlayer();
      navigateTo(nextRoute);
    });

    spVideo.addEventListener('seeking', function () {
      spSyncAudio(true);
    });

    spVideo.addEventListener('play', function () {
      if (spCurrentGrade !== 'normal') spPlayAltTrack(spCurrentGrade);
    });

    spVideo.addEventListener('pause', function () {
      if (spCurrentGrade !== 'normal' && spAudio) spAudio.pause();
    });
  }

  return {
    enter: function () {
      active = true;
      preloader.preloadSummary();

      summaryPlaybackFinished = false;
      spHasStarted = false;
      previousSummaryScreen = -1;

      if (mediaSrc) {
        if (video.getAttribute('src') !== mediaSrc) {
          video.src = mediaSrc;
        }
        try { video.muted = true; } catch (e) {}
        try { video.currentTime = 0; } catch (e) {}
        video.play().catch(function () {});
      }

      setScreen(0);
      armSummaryPlayback();
    },
    leave: function () {
      active = false;

      if (firstPointerHandler) {
        summaryRoot.removeEventListener('pointerdown', firstPointerHandler, true);
        firstPointerHandler = null;
      }

      video.pause();
      hideSummaryPlayer();
      clearSummaryRefDelay();

      if (refModal) refModal.hidden = true;
      if (refBtn) refBtn.hidden = true;
    }
  };
}
